import React, { useState } from 'react';
import axios from 'axios';
import { Wand2, Check, Image as ImageIcon } from 'lucide-react';
import { API, withCreds, inputCls, selectCls, btnPrimary, btnSecondary, Field, Modal, errMsg } from './AdminUI';
import ImageManager from './ImageManager';
import { useAIStatus } from './AIAssist';
import Select from '../FlatSelect';

const SCENES = [
  'on a white marble kitchen counter with soft morning light',
  'on a rustic wooden table in a cozy cafe',
  'on a minimalist shelf in a bright modern living room',
  'outdoors on a picnic blanket in a sunny park',
  'on a clean studio backdrop with a soft shadow',
  'on a desk next to a laptop and a plant',
];
const BLANKS = [
  'a white ceramic 11oz coffee mug', 'a black cotton crew-neck t-shirt laid flat', 'a natural canvas tote bag',
  'a white throw pillow on a sofa', 'a framed poster on a living room wall', 'a stainless steel water bottle',
];

function PhotoPicker({ images, value, onChange }) {
  if (!images.length) return <p className="text-ink/55 text-sm">Add at least one product photo first.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {images.map(img => (
        <button type="button" key={img} onClick={() => onChange(img)}
          className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${value === img ? 'border-honey' : 'border-transparent opacity-60 hover:opacity-100'}`}>
          <img src={img} alt="" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
}

export default function MockupModal({ images = [], onClose, onAdd }) {
  const status = useAIStatus();
  const [tab, setTab] = useState('lifestyle');
  const [source, setSource] = useState(images[0] || '');
  const [scene, setScene] = useState(SCENES[0]);
  const [design, setDesign] = useState([]);
  const [baseMode, setBaseMode] = useState(images.length ? 'photo' : 'blank');
  const [blank, setBlank] = useState(BLANKS[0]);
  const [extra, setExtra] = useState('');
  const [count, setCount] = useState(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);

  const generate = async () => {
    setBusy(true); setError('');
    try {
      const r = tab === 'lifestyle'
        ? await axios.post(`${API}/admin/ai/mockups/lifestyle`, { image_url: source, prompt: scene, count }, withCreds)
        : await axios.post(`${API}/admin/ai/mockups/design`, {
          design_url: design[0], product_image_url: baseMode === 'photo' ? source : '',
          blank_description: baseMode === 'blank' ? blank : '', prompt: extra, count,
        }, withCreds);
      setResults(rs => [...r.data.images, ...rs]);
      setPicked(p => [...r.data.images, ...p]);
    } catch (e) { setError(errMsg(e, 'Generation failed')); }
    finally { setBusy(false); }
  };

  const available = tab === 'lifestyle' ? status?.lifestyle_mockups : status?.design_mockups;
  const canGenerate = available && !busy && (tab === 'lifestyle' ? !!source && scene.trim() : design.length && (baseMode === 'blank' ? blank.trim() : source));
  const toggle = (u) => setPicked(p => (p.includes(u) ? p.filter(x => x !== u) : [...p, u]));

  return (
    <Modal title="Generate mockups" onClose={onClose} width="max-w-3xl">
      <div className="flex gap-2 mb-5">
        {[['lifestyle', 'Lifestyle scene'], ['design', 'Design on product']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setCount(k === 'design' ? 1 : 2); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium border ${tab === k ? 'bg-honey/10 text-honey-700 border-honey/30' : 'bg-ink/[0.04] text-ink/60 border-ink/10'}`}>{l}</button>
        ))}
      </div>

      {status && !available && (
        <p className="text-amber-600/80 text-sm mb-4">
          {tab === 'lifestyle'
            ? 'Lifestyle scenes need Cloudinary configured (and generative AI enabled on your Cloudinary plan).'
            : 'Design mockups need OPENAI_API_KEY in the backend environment, plus Cloudinary for storing the results.'}
        </p>
      )}

      {tab === 'lifestyle' ? (
        <div className="space-y-4">
          <p className="text-ink/55 text-xs">Keeps your real product and replaces the background with a generated scene. Works best with a clean photo of a single item.</p>
          <Field label="Product photo"><PhotoPicker images={images} value={source} onChange={setSource} /></Field>
          <Field label="Scene">
            <textarea value={scene} onChange={e => setScene(e.target.value)} rows={2} className={`${inputCls} resize-none`} />
            <div className="flex flex-wrap gap-1 mt-2">
              {SCENES.map(s => <button type="button" key={s} onClick={() => setScene(s)} className="text-xs px-2 py-1 rounded-full bg-ink/[0.04] text-ink/60 hover:text-ink">{s.replace(/^on (a |an )?/, '')}</button>)}
            </div>
          </Field>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-ink/55 text-xs">Places your artwork or logo onto a product. Each image costs a few cents on your OpenAI account and takes up to a minute.</p>
          <Field label="Artwork / logo" hint="PNG with a transparent background works best">
            <ImageManager images={design} onChange={setDesign} multiple={false} subfolder="designs" />
          </Field>
          <Field label="Put it on">
            <div className="flex gap-4 mb-2 text-sm">
              <label className="flex items-center gap-2 text-ink/75 cursor-pointer"><input type="radio" checked={baseMode === 'photo'} onChange={() => setBaseMode('photo')} className="accent-honey" disabled={!images.length} /> One of this product's photos</label>
              <label className="flex items-center gap-2 text-ink/75 cursor-pointer"><input type="radio" checked={baseMode === 'blank'} onChange={() => setBaseMode('blank')} className="accent-honey" /> A described blank product</label>
            </div>
            {baseMode === 'photo' ? <PhotoPicker images={images} value={source} onChange={setSource} /> : (
              <>
                <input value={blank} onChange={e => setBlank(e.target.value)} className={inputCls} />
                <div className="flex flex-wrap gap-1 mt-2">
                  {BLANKS.map(b => <button type="button" key={b} onClick={() => setBlank(b)} className="text-xs px-2 py-1 rounded-full bg-ink/[0.04] text-ink/60 hover:text-ink">{b.replace(/^an? /, '')}</button>)}
                </div>
              </>
            )}
          </Field>
          <Field label="Extra direction (optional)">
            <input value={extra} onChange={e => setExtra(e.target.value)} placeholder="e.g. place the design on the left chest, small" className={inputCls} />
          </Field>
        </div>
      )}

      <div className="flex items-center gap-3 mt-5">
        <Select value={count} onChange={e => setCount(Number(e.target.value))} className={selectCls}>
          {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} image{n > 1 ? 's' : ''}</option>)}
        </Select>
        <button type="button" onClick={generate} disabled={!canGenerate} className={btnPrimary}>
          <Wand2 className="w-4 h-4" /> {busy ? 'Generating… this can take a minute' : 'Generate'}
        </button>
      </div>
      {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl mt-3">{error}</p>}

      {results.length > 0 && (
        <div className="mt-6 border-t border-ink/10 pt-4">
          <p className="text-ink/70 text-sm mb-3">Click to choose which images to add to the product.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {results.map(u => (
              <button type="button" key={u} onClick={() => toggle(u)}
                className={`relative aspect-square rounded-xl overflow-hidden border-2 ${picked.includes(u) ? 'border-honey' : 'border-ink/10 opacity-60'}`}>
                <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
                {picked.includes(u) && <span className="absolute top-1 right-1 bg-honey text-black rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button type="button" onClick={() => { onAdd(picked); onClose(); }} disabled={!picked.length} className={btnPrimary}>
              <ImageIcon className="w-4 h-4" /> Add {picked.length} to product
            </button>
            <button type="button" onClick={onClose} className={btnSecondary}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
